import { type CSSProperties, forwardRef, Suspense } from 'react';

import classnames from 'classnames';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui/Tooltip';

import { Icon } from '../icon';
import * as styles from './Icon.css';
import type { IconElementProps } from './Icon.types';

const Base = forwardRef<HTMLDivElement, IconElementProps>((props, ref) => {
  const { element, children, attributes, className } = props;
  const { icon, size, fill, backgroundColor, padding, borderRadius, borderWidth, borderColor, borderStyle, count } =
    element.attrs || {};

  return (
    <div
      ref={ref}
      className={classnames(
        className,
        'relative',
        styles.iconContainer,
        count ? 'flex items-center justify-center' : '',
      )}
      style={
        {
          '--padding': padding,
          '--size': size,
          '--fill': fill,
          '--background-color': backgroundColor,
          '--border-width': borderWidth,
          '--border-color': borderColor,
          '--border-style': borderStyle,
          '--border-radius': borderRadius,
        } as CSSProperties
      }
      {...attributes}
    >
      {icon && <Icon type={icon.iconType} name={icon.iconName} size={size} />}
      {count && <span className="text-xs text-inherit">{count}</span>}
      {children}
    </div>
  );
});

export function IconElement(props: IconElementProps) {
  const { element, isLive = true } = props;

  return (
    <Suspense fallback={<Base {...props} />}>
      {element.attrs?.tooltip && isLive ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Base {...props} />
            </TooltipTrigger>
            <TooltipContent>
              <div className="p-1 rounded-md border text-xs font-medium bg-black text-white leading-none">
                {element.attrs?.tooltip?.text}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <Base {...props} />
      )}
    </Suspense>
  );
}
